import { SearchOutlined } from '@ant-design/icons';
import { Input } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { defaultTo, equals, gt, path } from 'ramda';
import { useDebounceFn } from 'ahooks';

const SearchBar = ({
  searchTerm,
  setSearchTerm,
  placeholder = 'Recherche',
  disabled = false,
  className = 'my-4',
  iconPosition = 'suffix',
  // 0 (default) propagates every keystroke to the parent, which is what callers doing
  // in-memory filtering want. A value > 0 notifies the parent only once the user stops
  // typing, so a server-backed search does not fire one request per keystroke.
  debounceWait = 0
}) => {
  const isDebounced = gt(debounceWait, 0);
  const [value, setValue] = useState(defaultTo('', searchTerm));
  // Last value pushed up to the parent. Lets us tell an external reset (e.g. a
  // "reset filters" button) apart from the parent merely lagging behind the input
  // during the debounce window — the latter must not clobber what is being typed.
  const lastPropagatedRef = useRef(defaultTo('', searchTerm));

  const propagate = useCallback((next) => {
    lastPropagatedRef.current = next;
    setSearchTerm(next);
  }, [setSearchTerm]);

  const { run: propagateDebounced, cancel: cancelPropagate } = useDebounceFn(propagate, { wait: debounceWait });

  useEffect(() => {
    const incoming = defaultTo('', searchTerm);
    if (equals(incoming, lastPropagatedRef.current)) return;
    cancelPropagate();
    lastPropagatedRef.current = incoming;
    setValue(incoming);
  }, [searchTerm, cancelPropagate]);

  const handleChange = (event) => {
    const next = defaultTo('', path(['target', 'value'], event));
    setValue(next);
    return isDebounced ? propagateDebounced(next) : propagate(next);
  };

  const icon = <SearchOutlined />;

  return (
    <Input
      className={className}
      placeholder={placeholder}
      value={value}
      onChange={handleChange}
      disabled={disabled}
      allowClear
      prefix={equals('prefix', iconPosition) ? icon : undefined}
      suffix={equals('suffix', iconPosition) ? icon : undefined}
    />
  );
};

export default SearchBar;
